import { IllustrationProduct } from "../data/illustrationInterface";
import { IProductInfo, MinMaxValues } from "../data/productInfoInterfaces";
import { appConfig } from "@/common/config/appConfig";
import { CoverageItem, FormikTermData, IllustrationInfo, Insured, PersonInfo } from "@/common/data/formikData";
import { IQuoteDto, Sex } from "@/common/data/quoteInterfaces";
import { ValidationMode } from "@/components/term/contexts/renderModelProvider";
import { FormikProps } from "formik";
import React from "react";

export const getAgeNearest = (dob: any, backDatedDate?: Date | null) => {
  let ageNearest;
  let previousBirthday: any;
  let nextBirthday: any;
  const dateOfBirth = new Date(dob);
  const monthOfBirth = dateOfBirth.getUTCMonth();
  const dayOfBirth = dateOfBirth.getUTCDate();

  const currentDay = backDatedDate ?? new Date();

  const today: any = new Date(
    currentDay.getUTCFullYear(),
    currentDay.getUTCMonth(),
    currentDay.getUTCDate(),
    0,
    0,
    0
  );

  const birthdayThisYear = new Date(
    Date.UTC(today.getUTCFullYear(), monthOfBirth, dayOfBirth)
  );

  previousBirthday = new Date(birthdayThisYear);
  nextBirthday = new Date(birthdayThisYear);

  if (birthdayThisYear > today) {
    previousBirthday.setUTCFullYear(previousBirthday.getUTCFullYear() - 1);
  } else {
    nextBirthday.setUTCFullYear(nextBirthday.getUTCFullYear() + 1);
  }

  ageNearest = today - previousBirthday > nextBirthday - today ? calculateAge(dateOfBirth, today) + 1 : calculateAge(dateOfBirth, today);

  return ageNearest;
};

export const calculateAge = (
  dateOfBirth: any,
  currentDate: any = new Date()
) => {
  const birthDate = new Date(dateOfBirth);
  const now = currentDate || new Date();
  let age = now.getUTCFullYear() - birthDate.getUTCFullYear();

  // Adjust age if the birthdate hasn't occurred yet this year
  if (
    now.getUTCMonth() < birthDate.getUTCMonth() ||
    (now.getUTCMonth() === birthDate.getUTCMonth() &&
      now.getUTCDate() < birthDate.getUTCDate())
  ) {
    age--;
  }

  return age;
};

export const getBackdatedDateToSaveAge = () => {
  const currentDate = new Date();
  const currentyear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1; // Month is 0 based

  if (isLeapYear(currentyear) && currentMonth >= 3) {
    currentDate.setDate(currentDate.getDate() - 365);
  } else {
    currentDate.setDate(currentDate.getDate() - 364);
  }

  return currentDate;
};

export const isLeapYear = (year: number): boolean => {
  if (
    (year % 4 === 0 && year % 100 !== 0) ||
    (year % 4 === 0 && year % 100 === 0 && year % 400 === 0)
  ) {
    return true;
  }

  return false;
};

export const newEntityId = (list: { id: number }[]): number => {
  const minValue =
        list
          .map((a) => a.id)
          .reduce((acc, currValue) => (Math.min(currValue, acc)), 0) ?? 0;

  return minValue - 1;
};

// use this function for test output stringified value to console or template
export const toJson = (value: unknown, formatted: boolean = false) => (
  value === undefined
    ? "UNDEFINED"
    : value === null
      ? "NULL"
      : formatted
        ? JSON.stringify(value, null, " ".length)
        : JSON.stringify(value)
);

export const formatCurrencyForInput = (value: any) => {
  if (value != null && typeof Number(value) === "number") {
    const formatWithComma = value
      .toString()
      .replaceAll(/\B(?=(\d{3})+(?!\d))/g, ",");

    return formatWithComma;
  }

  return "";
};

export const formatCurrencyForDisplay = (
  value: number | null,
  showDollarSign: boolean = true,
  showDecimal: boolean = true
) => {
  if (value === 0 || value == null) {
    return "--";
  }

  return `${showDollarSign ? "$" : ""}${formatCurrencyForInput(
    showDecimal ? value.toFixed(2) : value
  )}`;
};

export const formatNumberFrench = (value: any) => {
  if (value != null && typeof Number(value) === "number") {
    const formatWithNonBreakingSpace = value
      .toString()
      .replaceAll(/\B(?=(\d{3})+(?!\d))/g, "\u00A0");

    return formatWithNonBreakingSpace;
  }

  return "";
};

export const formatCurrencyForDisplayInFrench = (
  value: number | null,
  showDollarSign: boolean = true,
  showDecimal: boolean = true
) => {
  if (value === 0 || value == null) {
    return "--";
  }

  return `${formatNumberFrench(
    showDecimal ? value && value.toFixed(2).replace(".", ",") : value
  )} ${showDollarSign ? "$" : ""}`;
};

export const stripCurrencyFormattingForFrench = (value: string) => {
  const numericValue = Number(value.replaceAll("\u00A0", "")) || null;

  return numericValue;
};

export const stripCurrencyFormatting = (value: string) => {
  const numericValue = Number(value.replaceAll(",", "")) || null;

  return numericValue;
};

export const restrictCharacterLength = (
  value: string,
  maxLength: number
): string => {
  if (value.length > maxLength) {
    return value.slice(0, maxLength);
  }

  return value;
};

export const GenerateAndOpenPdf = (data: string) => {
  const binaryString = atob(data.split(",")[1]);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }

  const blob = new Blob([bytes], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  window.open(url, "_blank");
};

export const getInsuredFullName = (insured: Insured): string => {
  const result =
        `${insured?.firstName} ${insured?.middleName} ${insured?.lastName}`
          .replaceAll(/  +/g, " ")
          .trim();

  return result;
};

export const checkValidationForAddPayor = (formik: any, riderType: string, payorMinMaxAge: MinMaxValues) => {
  let isError = false;

  const riderDetails =
        formik.values?.[riderType]?.payorDisabilityWaiverOfPremiumRiderInfo;
  if (
    !isNumberValue(riderDetails?.age) ||
    riderDetails?.age < payorMinMaxAge.min ||
    riderDetails?.age > payorMinMaxAge.max
  ) {
    formik.setFieldTouched(
      `${riderType}.payorDisabilityWaiverOfPremiumRiderInfo.age`,
      true,
      true
    );
    isError = true;
  }

  if (riderDetails?.sex !== Sex.Male && riderDetails?.sex !== Sex.Female) {
    formik.setFieldTouched(
      `${riderType}.payorDisabilityWaiverOfPremiumRiderInfo.sex`,
      true,
      true
    );
    isError = true;
  }

  if (typeof (riderDetails?.isSmoker) !== "boolean") {
    formik.setFieldTouched(
      `${riderType}.payorDisabilityWaiverOfPremiumRiderInfo.isSmoker`,
      true,
      true
    );
    isError = true;
  }

  return isError;
};

export const asNumberValue = (value: unknown): number =>
  (isNumberValue(value) ? value : null as unknown as number);

export const isNumberValue = (value: any): value is number =>
  value != null && Number.isFinite(value);

export const isBooleanValue = (value: any): value is boolean =>
  typeof value === "boolean";

export const isStringValue = (value: unknown, allowEmpty: boolean = false): value is string =>
  value != null && typeof value === "string" && (allowEmpty || value.length > 0);

export const isObjectValue = (value: unknown): value is object =>
  value != null && typeof value === "object" && !Array.isArray(value);

export const isArrayValue = (value: unknown): value is unknown[] =>
  value != null && typeof value === "object" && Array.isArray(value);

export const scrollToError = () => {
  const element = window.document.querySelector("[data-error-anchor]");
  element?.scrollIntoView({
    behavior: "smooth",
    block: "center",
    inline: "nearest"
  });
};

export const isFunction = (value: unknown): value is "function" =>
  typeof value === "function";

export const invoke = (callback: ((...args: never[]) => unknown) | undefined, ...args: unknown[]): unknown =>
  isFunction(callback) ? callback(...(args as never[])) : undefined;

export const allowedNameCharacters =
    "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZàâäèéêëîïôœùûüÿçÀÂÄÈÉÊËÎÏÔŒÙÛÜŸÇ-'. ";

export const validateInput = (value: string) => {
  for (const char of value) {
    if (!allowedNameCharacters.includes(char)) {
      return false;
    }
  }

  return true;
};

const specialKeysList = [
  "Backspace", "Tab", "Enter", "Shift", "Control", "Alt", "Pause", "CapsLock", "Escape",
  "PageUp", "PageDown", "End", "Home", "ArrowLeft", "ArrowUp", "ArrowRight", "ArrowDown", "PrintScreen", "Insert",
  "Delete", "Meta", "ContextMenu", "F1", "F2", "F3", "F4", "F5", "F6", "F7", "F8", "F9", "F10", "F11", "F12",
  "NumLock", "ScrollLock", "AudioVolumeMute", "AudioVolumeDown", "AudioVolumeUp",
  "LaunchMediaPlayer", "LaunchApplication1", "LaunchApplication2"
];

export const isSpecialKey = (key: string): boolean => specialKeysList.includes(key);

export const validateNumberInput = (
  value: string,
  decimalSeparator?: string,
  allowNegative: boolean = false
) => {
  if (decimalSeparator != null && decimalSeparator !== "" && decimalSeparator !== "." && decimalSeparator !== ",") {
    return false;
  }

  const allowDecimal = decimalSeparator != null && decimalSeparator !== "";
  const separator = decimalSeparator === "." ? "\\." : decimalSeparator;
  const regexp = new RegExp(`^${allowNegative ? "-?" : ""}\\d*${allowDecimal ? `${separator}?\\d*` : ""}$`);
  const result = regexp.test(value || "");

  return result;
};

export const nameKeyDownFilter = (event: React.KeyboardEvent<HTMLInputElement>) => {
  const result = isSpecialKey(event.key) || validateInput(event.key);
  if (!result) {
    event.preventDefault();
  }

  return result;
};

export const numberKeyDownFilter = (event: React.KeyboardEvent<HTMLInputElement>, decimalSeparator: string) => {
  const result = isSpecialKey(event.key) || validateNumberInput(event.key, decimalSeparator);
  if (!result) {
    event.preventDefault();
  }

  return result;
};

export const checkIncompleteInfo = (
  insuredIndex: number,
  renderModel: any,
  formik: FormikProps<FormikTermData>
) => {
  const { validation, model, validationMode } = renderModel ?? {};
  const { values, errors, touched } = formik ?? {};

  const insuredValidation = validation?.[insuredIndex];
  const basePlanValidation = model?.basePlansInfoToRender;

  const formikValues = values?.insureds?.[insuredIndex];
  const formikErrors = errors?.insureds?.[insuredIndex];
  const formikTouched = touched?.insureds?.[insuredIndex];

  const selectedPlans =
        insuredValidation?.noSelectedBasePlan &&
        ((insuredValidation.isDirty &&
          validationMode >= ValidationMode.viewReport) ||
          formikValues?.riderItems?.length > 0);

  const invalidPlans = basePlanValidation?.[insuredIndex]
    ?.map((a: any) => {
      const validation = insuredValidation?.plans[a.plan.id];
      const result = {
        isSelected: validation.isSelected,
        invalidAge: validation.invalidAge,
        minAge: validation.minAge,
        maxAge: validation.maxAge
      };

      return result;
    })
    .filter((a: any) => a.invalidAge)
    .sort((a: any, b: any) =>
      a.maxAge > b.maxAge ? -1 : a.maxAge < b.maxAge ? 1 : 0
    )
    ?.filter((a: any) => a.isSelected);

  const invalidRiders = formikValues?.riderItems
    .map((a: CoverageItem) => {
      const validation = insuredValidation?.plans[a.planId];
      const result = {
        invalidAge: validation?.invalidAge
      };

      return result;
    })
    .filter((a) => a.invalidAge);

  const inCompleteRatingInfo = insuredValidation?.inCompleteRatingInfo;

  return (
    (formikErrors && formikTouched && insuredValidation?.hasErrors) ||
    insuredValidation?.totalCoverageExceeded ||
    invalidPlans?.length > 0 ||
    invalidRiders?.length > 0 ||
    (formikErrors && formikTouched && insuredValidation?.isDirty) ||
    selectedPlans ||
    inCompleteRatingInfo
  );
};

export const isUserAuthenticated = () => {
  const result = document.cookie
    .split(";")
    .some(a => a.trim().startsWith(appConfig.AUTH0_COOKIE_NAME));

  return result;
};

export const getRequestHeaders = (): HeadersInit => {
  return {
    "Accept-Language": "en",
    "Content-Type": "application/json;charset=UTF-8",
    "Ocp-Apim-Subscription-Key": appConfig.SANDBOX_MODE === "true" ? appConfig.BFF_SANDBOX_SUBSCRIPTION_KEY : appConfig.BFF_SUBSCRIPTION_KEY
  };
};

export const updateQuoteDtoWithIllustrationInfo = (illustrationInfo: IllustrationInfo | undefined, dto: IQuoteDto): IQuoteDto => {
  if (illustrationInfo == null) {
    return dto;
  }

  dto = { ...dto, ...illustrationInfo };

  return dto;
};

export const getExternalServicesIds = (code: string) => {
  const productName = code?.toLowerCase();
  const termIllustration = IllustrationProduct.Term.toLowerCase();
  const ciIllustration = IllustrationProduct.CriticalIllness.toLowerCase();

  let alidaId = "";
  let gtmId = "";
  const heapId = appConfig.HEAP_ANALYTICS_APP_ID;

  if (productName === termIllustration) {
    alidaId = appConfig.ALIDA_TERM_ID;
    gtmId = appConfig.GTM_TERM_ID;
  } else if (productName === ciIllustration) {
    alidaId = appConfig.ALIDA_CI_ID;
    gtmId = appConfig.GTM_CI_ID;
  }

  return {
    alidaId,
    gtmId,
    heapId
  };
};

export const focusOnPlanCoverageAmount = () => {
  const element = window.document.querySelector("[data-focus-anchor]");
  element?.scrollIntoView({
    behavior: "smooth",
    block: "center",
    inline: "nearest"
  });
  // eslint-disable-next-line unicorn/prefer-query-selector
  document.getElementById("insured.basePlanItems[0].faceAmount")?.focus();
};

export const validateDwpRidersAvailabilityBasedOnTotalPremiums = (
  totalPremium: number | null,
  maxTotalYearlyPremiumAmountForDisabilityWaiverOfPremium: number,
  planCode: string,
  dwpCodes: string[]
): boolean => {
  if (isNumberValue(totalPremium) && totalPremium >
    maxTotalYearlyPremiumAmountForDisabilityWaiverOfPremium &&
    dwpCodes.includes(planCode)) {
    return false;
  }

  return true;
};

export const isDwpRider = (
  dwpCodes: string[],
  planCode: string
): boolean => {
  const isDwpRider = dwpCodes
    .includes(planCode);

  return isDwpRider;
};

export const checkBackDate = (item: PersonInfo | null | undefined, productInfo: IProductInfo): boolean => {

  if (!item?.dateOfBirth) {
    return false;
  }

  const ageNearest = getAgeNearest(item.dateOfBirth);
  const backDatedAge = calculateAge(item.dateOfBirth);
  const result =
      ageNearest >= productInfo.minAge &&
      ageNearest <= productInfo.maxAge &&
      ageNearest != backDatedAge;

  return result;
};

export const hasTerm10Term20 = (
  TermCodes: string[],
  planCode: string
): boolean => {
  const hasTerm10Term20 = TermCodes
    .includes(planCode);

  return hasTerm10Term20;
};

export const sortByDisplayOrder = (a: { displayOrder: number }, b: { displayOrder: number }): number => {
  const order = +a.displayOrder > +b.displayOrder ? 1 : +a.displayOrder < +b.displayOrder ? -1 : 0;

  return order;
};

export const getFormattedDate = (language: string) => {
  const date = new Date();
  const isFrench = language === 'fr';

  if (isFrench) {
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${date.getDate()} ${new Intl.DateTimeFormat('fr-CA', { month: 'short' }).format(date)} ${date.getFullYear()} à ${hours}:${minutes}:${seconds}`;
  } else {
    return date.toLocaleString(language, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      hour12: true
    });
  }
};
